import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Trash2, Image, Calendar as CalendarIcon, Clock } from 'lucide-react';
import { format } from 'date-fns';
import { motion } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PhotographerDashboard = () => {
  const navigate = useNavigate();
  const { photographer, isPhotographer } = useAuth();
  const [activeTab, setActiveTab] = useState('availability');
  
  // Availability state
  const [availability, setAvailability] = useState([]);
  const [newDate, setNewDate] = useState(undefined);
  const [newStartTime, setNewStartTime] = useState('09:00');
  const [newEndTime, setNewEndTime] = useState('17:00');
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  
  // Gallery state
  const [galleryPosts, setGalleryPosts] = useState([]);
  const [newPostTitle, setNewPostTitle] = useState('');
  const [newPostDescription, setNewPostDescription] = useState('');
  const [newPostImages, setNewPostImages] = useState('');
  const [loadingGallery, setLoadingGallery] = useState(false);

  useEffect(() => {
    if (!isPhotographer) {
      navigate('/photographer/login');
      return;
    }
    fetchAvailability();
    fetchGalleryPosts();
  }, [isPhotographer]);

  const fetchAvailability = async () => {
    try {
      const response = await fetch(`${API}/photographer/availability`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setAvailability(data);
      }
    } catch (error) {
      console.error('Error fetching availability:', error);
    }
  };

  const fetchGalleryPosts = async () => {
    try {
      const response = await fetch(`${API}/gallery`);
      if (response.ok) {
        const data = await response.json();
        // Filter to show only this photographer's posts
        const myPosts = data.filter(p => p.photographer_id === photographer?.photographer_id);
        setGalleryPosts(myPosts);
      }
    } catch (error) {
      console.error('Error fetching gallery:', error);
    }
  };

  const handleAddAvailability = async () => {
    if (!newDate) {
      toast.error('Please select a date');
      return;
    }

    setLoadingAvailability(true);
    try {
      const response = await fetch(`${API}/photographer/availability`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          date: format(newDate, 'yyyy-MM-dd'),
          start_time: newStartTime,
          end_time: newEndTime
        })
      });

      if (!response.ok) throw new Error('Failed to add availability');

      toast.success('Availability added!');
      fetchAvailability();
      setNewDate(undefined);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoadingAvailability(false);
    }
  };

  const handleDeleteAvailability = async (availabilityId) => {
    try {
      const response = await fetch(`${API}/photographer/availability/${availabilityId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to delete availability');

      toast.success('Availability removed');
      fetchAvailability();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleAddGalleryPost = async () => {
    if (!newPostTitle || !newPostImages) {
      toast.error('Please fill in title and at least one image URL');
      return;
    }

    setLoadingGallery(true);
    try {
      const imageUrls = newPostImages.split('\n').filter(url => url.trim());
      
      const response = await fetch(`${API}/gallery`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title: newPostTitle,
          description: newPostDescription,
          image_urls: imageUrls,
          snake_ids: []
        })
      });

      if (!response.ok) throw new Error('Failed to create post');

      toast.success('Post added to gallery!');
      fetchGalleryPosts();
      setNewPostTitle('');
      setNewPostDescription('');
      setNewPostImages('');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoadingGallery(false);
    }
  };

  const handleDeletePost = async (postId) => {
    try {
      const response = await fetch(`${API}/gallery/${postId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to delete post');

      toast.success('Post deleted');
      fetchGalleryPosts();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const timeSlots = [
    '08:00', '09:00', '10:00', '11:00', '12:00', '13:00', 
    '14:00', '15:00', '16:00', '17:00', '18:00', '19:00', '20:00'
  ];

  if (!isPhotographer) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background" data-testid="photographer-dashboard">
      <Navbar />
      
      <div className="pt-24 pb-16 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold mb-2">
              Welcome, <span className="gradient-text">{photographer?.name}</span>
            </h1>
            <p className="text-muted-foreground">Manage your availability and gallery posts</p>
          </div>

          {/* Tab Navigation */}
          <div className="flex space-x-4 mb-8 border-b border-border">
            <button
              onClick={() => setActiveTab('availability')}
              className={`pb-4 px-4 font-medium transition-colors ${
                activeTab === 'availability'
                  ? 'text-primary border-b-2 border-primary'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid="tab-availability"
            >
              <CalendarIcon className="w-4 h-4 inline mr-2" />
              Availability
            </button>
            <button
              onClick={() => setActiveTab('gallery')}
              className={`pb-4 px-4 font-medium transition-colors ${
                activeTab === 'gallery'
                  ? 'text-primary border-b-2 border-primary'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid="tab-gallery"
            >
              <Image className="w-4 h-4 inline mr-2" />
              Gallery Posts
            </button>
          </div>

          {/* Availability Tab */}
          {activeTab === 'availability' && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="grid md:grid-cols-2 gap-8"
            >
              {/* Add Availability */}
              <Card className="bg-card/50 border-white/10">
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Plus className="w-5 h-5 mr-2 text-primary" />
                    Add Availability
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Select Date</Label>
                    <Calendar
                      mode="single"
                      selected={newDate}
                      onSelect={setNewDate}
                      disabled={(date) => date < new Date()}
                      className="rounded-md border border-border mt-2"
                      data-testid="availability-calendar"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Start Time</Label>
                      <Select value={newStartTime} onValueChange={setNewStartTime}>
                        <SelectTrigger data-testid="start-time-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {timeSlots.map((time) => (
                            <SelectItem key={time} value={time}>{time}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>End Time</Label>
                      <Select value={newEndTime} onValueChange={setNewEndTime}>
                        <SelectTrigger data-testid="end-time-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {timeSlots.map((time) => (
                            <SelectItem key={time} value={time}>{time}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <Button
                    onClick={handleAddAvailability}
                    disabled={loadingAvailability || !newDate}
                    className="w-full btn-primary"
                    data-testid="add-availability-btn"
                  >
                    {loadingAvailability ? 'Adding...' : 'Add Availability'}
                  </Button>
                </CardContent>
              </Card>

              {/* Current Availability */}
              <Card className="bg-card/50 border-white/10">
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Clock className="w-5 h-5 mr-2 text-primary" />
                    Your Availability
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {availability.length === 0 ? (
                    <p className="text-muted-foreground text-center py-8">
                      No availability set yet
                    </p>
                  ) : (
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {availability.map((slot) => (
                        <div
                          key={slot.availability_id}
                          className={`flex items-center justify-between p-3 rounded-lg ${
                            slot.is_booked ? 'bg-secondary/20' : 'bg-muted'
                          }`}
                          data-testid={`availability-slot-${slot.availability_id}`}
                        >
                          <div>
                            <p className="font-medium">{slot.date}</p>
                            <p className="text-sm text-muted-foreground">
                              {slot.start_time} - {slot.end_time}
                            </p>
                            {slot.is_booked && (
                              <span className="text-xs text-secondary">Booked</span>
                            )}
                          </div>
                          {!slot.is_booked && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteAvailability(slot.availability_id)}
                              data-testid={`delete-availability-${slot.availability_id}`}
                            >
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Gallery Tab */}
          {activeTab === 'gallery' && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-8"
            >
              {/* Add New Post */}
              <Card className="bg-card/50 border-white/10">
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Plus className="w-5 h-5 mr-2 text-primary" />
                    Post New Work
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="postTitle">Title</Label>
                    <Input
                      id="postTitle"
                      value={newPostTitle}
                      onChange={(e) => setNewPostTitle(e.target.value)}
                      placeholder="e.g., Dark Elegance Shoot"
                      className="mt-1"
                      data-testid="post-title-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="postDescription">Description (Optional)</Label>
                    <Textarea
                      id="postDescription"
                      value={newPostDescription}
                      onChange={(e) => setNewPostDescription(e.target.value)}
                      placeholder="Describe the shoot..."
                      className="mt-1"
                      data-testid="post-description-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="postImages">Image URLs (one per line)</Label>
                    <Textarea
                      id="postImages"
                      value={newPostImages}
                      onChange={(e) => setNewPostImages(e.target.value)}
                      placeholder="https://example.com/image1.jpg&#10;https://example.com/image2.jpg"
                      className="mt-1 min-h-[100px]"
                      data-testid="post-images-input"
                    />
                  </div>
                  <Button
                    onClick={handleAddGalleryPost}
                    disabled={loadingGallery || !newPostTitle || !newPostImages}
                    className="btn-primary"
                    data-testid="add-post-btn"
                  >
                    {loadingGallery ? 'Posting...' : 'Post to Gallery'}
                  </Button>
                </CardContent>
              </Card>

              {/* Existing Posts */}
              <div>
                <h3 className="text-xl font-semibold mb-4">Your Posts</h3>
                {galleryPosts.length === 0 ? (
                  <Card className="bg-card/50 border-white/10 p-8 text-center">
                    <p className="text-muted-foreground">You haven't posted any work yet</p>
                  </Card>
                ) : (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {galleryPosts.map((post) => (
                      <Card
                        key={post.post_id}
                        className="overflow-hidden border-white/10"
                        data-testid={`gallery-post-${post.post_id}`}
                      >
                        <div className="relative h-48">
                          <img
                            src={post.image_urls[0]}
                            alt={post.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <CardContent className="p-4">
                          <div className="flex justify-between items-start">
                            <div>
                              <h4 className="font-semibold">{post.title}</h4>
                              {post.description && (
                                <p className="text-sm text-muted-foreground line-clamp-2">
                                  {post.description}
                                </p>
                              )}
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeletePost(post.post_id)}
                              data-testid={`delete-post-${post.post_id}`}
                            >
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PhotographerDashboard;
